import { t } from 'i18next';
import * as Lockr from 'lockr';

// constants
import { ReadOnly } from '../constants/Enums';

// actions
import { CatalogActions } from '../actions/CatalogActions';
import { ArticleActions } from '../actions/ArticleActions';
import { ProjectManagerActions } from '../actions/ProjectManagerActions';

// stores
import { KompanStore } from '../Kompan/KompanStore';

// utils
import { GlobalHelper } from '../utils/GlobalHelper';
import { UIHelper } from '../utils/UIHelper';
import { DownloadHelper } from '../utils/DownloadHelper';

// Kompan
import { KompanBase, VariantObject, PriceObject } from './KompanBase';
import { KompanHelper } from './KompanHelper';

// wcf
import utils = egr.wcf.utils;
import core = egr.wcf.core;
import cf = egr.wcf.cf;
import eaiws = egr.wcf.eaiws;
import { PropertyMap, SimpleProperty } from './PropertyCompare';

export abstract class KompanInterface {
    public static closeConfigurator(): void {
        KompanBase.Configuration.CloseWithoutVariant();
        setTimeout(window.close, 500);
    }

    public static initLanguage(): string {
        const tUiLang: string =
            (KompanBase.Configuration.UiLanguage || Lockr.get('language') || navigator.language || (navigator as any).userLanguage).substr(0, 2);
        Lockr.set('language', tUiLang);
        const tDataLang: string =
            (KompanBase.Configuration.DataLanguage || Lockr.get('dataLanguage') || navigator.language || (navigator as any).userLanguage).substr(0, 2);
        Lockr.set('dataLanguage', tDataLang);
        return tUiLang;
    }

    public static async insertArticle(): Promise<void> {
        if (!KompanBase.openedByQuoteIt) {
            return Promise.resolve();
        }
        if (utils.string.isNotNullOrEmpty(KompanBase.Configuration.BaseArticleNumber)) {
            const tVariantCode: string = KompanBase.Configuration.VariantCode || '';
            const tSearchParameter: eaiws.catalog.SearchArticleParameterSet = new eaiws.catalog.SearchArticleParameterSet();
            tSearchParameter.catalogIds = ['kom:0'];
            tSearchParameter.baseArticleNumber = KompanBase.Configuration.BaseArticleNumber;
            tSearchParameter.varCodeType = 'OFML';
            tSearchParameter.variantCode = tVariantCode;
            const tResult: eaiws.catalog.TopCatalogItems | undefined = await GlobalHelper.eaiwsSession.catalog.searchArticle(tSearchParameter);
            if (tResult && tResult.scoredItems.length > 0) {
                const item: eaiws.catalog.ArticleCatalogItem = tResult.scoredItems[0].item as eaiws.catalog.ArticleCatalogItem;
                item.varCodeType = 'OFML';
                item.variantCode = tVariantCode;
                await CatalogActions.enterCatalogItem(tResult.scoredItems[0].item);
                await KompanInterface.updatePrice();
                return Promise.resolve();
            }
        }
        const tErrorMessage: string = t('Item does not exist. The configurator will be closed.');
        return UIHelper.showMessage(tErrorMessage, () => {
            window.close();
        });
    }

    public static async reopenConfiguration(): Promise<void> {
        KompanHelper.storeValue('readOnly', ReadOnly.Reopen);
        const tReopenFile: string = KompanBase.Configuration.ReopenConfiguration;
        if (utils.string.getFileExtension(tReopenFile) === '.obx') {
            return KompanInterface.reopenOBXFile(tReopenFile);
        }

        // Insert article from given pbox url in shared object
        try {
            const tPboxUrl: string = KompanHelper.convertNetworkPathToUrl(tReopenFile);
            const tBuffer: ArrayBuffer = await DownloadHelper.getContent(tPboxUrl);
            const tContainer: Blob = new Blob([tBuffer], {type: 'application/zip'});
            await GlobalHelper.coreApp.document.loadFromZip(tContainer);
            ArticleActions.invalidateArticleList();
        } catch (pError) {
            const tErrorMessage: string = t('Error while loading variant. The configurator will be closed.');
            return UIHelper.showMessage(tErrorMessage, () => {
                window.close();
            });
        }

        // Zoom to fit element to screen and disable selection
        const tElement: core.mdl.SceneElement = GlobalHelper.coreApp.model.elements[0];
        const tView: core.view.View | null = GlobalHelper.coreApp.viewer.activeView;
        if (tView) {
            tView.cameraControl.zoomToFitElements([tElement]);
        }
        tElement.canSelect = (): boolean => { return (false); };

        // Update price stored price from pbox file
        const tPrice: PriceObject = GlobalHelper.coreApp.document.appData.getData('kompan', 'price') as PriceObject;
        KompanHelper.storeValue('price', tPrice);

        // Update stored variant from pbox file
        const tVariant: VariantObject = GlobalHelper.coreApp.document.appData.getData('kompan', 'variant') as VariantObject;
        KompanHelper.storeValue('variant', tVariant);

        try {
            // eslint-disable-next-line
            let initialProperties: PropertyMap | undefined = JSON.parse(
                GlobalHelper.coreApp.document.appData.getData<string>('kompan', 'storedInitialProperties')!,
                KompanHelper.jSonMapReviver) as PropertyMap;
            if (!initialProperties) {
                initialProperties = new Map<string, Map<string, Array<SimpleProperty>>>();
            }
            KompanHelper.storeValue('storedInitialProperties', initialProperties);
        } catch (error) {
            KompanHelper.storeValue('storedInitialProperties', new Map<string, Map<string, Array<SimpleProperty>>>());
        }

        try {
            // eslint-disable-next-line
            let finalProperties: PropertyMap | undefined = JSON.parse(
                GlobalHelper.coreApp.document.appData.getData<string>('kompan', 'storedFinalProperties')!,
                KompanHelper.jSonMapReviver) as PropertyMap;
            if (!finalProperties) {
                finalProperties = new Map<string, Map<string, Array<SimpleProperty>>>();
            }
            KompanHelper.storeValue('storedFinalProperties', finalProperties);
        } catch (error) {
            KompanHelper.storeValue('storedFinalProperties', new Map<string, Map<string, Array<SimpleProperty>>>());
        }
    }

    public static async updatePrice(): Promise<PriceObject | null> {
        if (!KompanBase.openedByQuoteIt) {
            return (null);
        }

        let tPrice: PriceObject | null = null;
        const tObxUrl: string = await DownloadHelper.exportObx();
        const tCalcPriceObj: any = {
            products: [
                {
                    product: KompanBase.Configuration.ProductNo,
                    ObxFileUrl: tObxUrl
                }
            ]
        };
        try {
            tPrice = await KompanBase.Configuration.WS_CalculatePrice(JSON.stringify(tCalcPriceObj));
            KompanHelper.storeValue('price', tPrice);
        } catch (pError) {
            // nothing to do here
        }

        return tPrice;
    }

    public static async createVariant(): Promise<void> {
        KompanHelper.storeValue('readOnly', ReadOnly.Save);
        try {
            const tElement: core.mdl.SceneElement = GlobalHelper.coreApp.model.elements[0];
            const tArticle: cf.ArticleElement = tElement as cf.ArticleElement;
            tElement.canSelect = (): boolean => { return (false); };
            GlobalHelper.coreApp.model.setSelection([]);

            // now make sure to get up-to-date price and variantId
            const tPrice: PriceObject | null = await KompanInterface.updatePrice();
            const tVariant: VariantObject | null = await KompanInterface.getVariantID();

            if (tVariant === null) {
                return;
            }

            // store prive and variant to pbox file for reopen purpose
            GlobalHelper.coreApp.document.appData.setData('kompan', 'price', tPrice);
            GlobalHelper.coreApp.document.appData.setData('kompan', 'variant', tVariant);
            await KompanHelper.storeVariantId(tArticle.basketId, tVariant.variantno);

            const storedInitialProperties: PropertyMap = KompanStore.getState().storedInitialProperties;
            GlobalHelper.coreApp.document.appData.setData(
                'kompan',
                'storedInitialProperties',
                JSON.stringify(storedInitialProperties, KompanHelper.jSonMapReplacer)
            );

            const storedFinalProperties: PropertyMap = KompanStore.getState().storedFinalProperties;
            GlobalHelper.coreApp.document.appData.setData(
                'kompan',
                'storedFinalProperties',
                JSON.stringify(storedFinalProperties, KompanHelper.jSonMapReplacer)
            );

            // get files needed for Kompan post processing
            const tLocalPathAndFilename: string = KompanHelper.convertToLocalPath(tVariant.path) + '/' + tVariant.filename;
            ProjectManagerActions.setCurrentProjectName(tVariant.filename);
            // const tPboxUrl: string = await KompanHelper.exportPBox(tLocalPathAndFilename);
            await KompanHelper.exportPBox(tLocalPathAndFilename);
            const tDwgUrl: string = await KompanHelper.exportDWG();
            const tImageUrl: string = await KompanHelper.getPreviewImage(tArticle);
            const obxUrl: string = await GlobalHelper.articleManager.exportObx([tArticle as cf.MainArticleElement]);
            const tXml: string = await KompanHelper.writeKompanXML(tVariant, tPrice);

            // Upload files to Kompan server
            KompanHelper.uploadContentToFileStorage(tXml, tLocalPathAndFilename, 'xml', false);
            KompanHelper.uploadUrlToFileStorage(tImageUrl, tLocalPathAndFilename);
            KompanHelper.uploadUrlToFileStorage(tDwgUrl, tLocalPathAndFilename);
            KompanHelper.uploadUrlToFileStorage(obxUrl, tLocalPathAndFilename);

            // Prepare object and send to QuoteIt
            const tArticleData: eaiws.basket.ArticleData = await tArticle.getArticleData();
            const tItemProperties: eaiws.basket.ItemProperties = await tArticle.getItemProperties();

            const tFinalArticleNumber: string =
                tItemProperties && tItemProperties.article && tItemProperties.article.finalArticleNumber ? tItemProperties.article.finalArticleNumber : '';
            let tDescription: string = '';
            let tFeatureText: string = tArticleData.featureText;

            const tRegex: RegExp = /(^Reference Remark$|^Reference Remark:(.*))\n?/gm;
            const tResult: RegExpExecArray | null = tRegex.exec(tFeatureText);
            if (tResult) {
                tDescription = tResult[2] || '';
                tDescription = tDescription.trim();
                tFeatureText = tFeatureText.replace(tRegex, '');
            }

        // try {
            await KompanBase.Configuration.WS_CreateVariant(
                KompanBase.Configuration.ProductNo,
                tVariant.variantno,
                tVariant.path,
                tVariant.filename,
                tPrice === null ? -1 : tPrice.value,
                tDescription,
                tFinalArticleNumber,
                tFeatureText
            );
        } catch (error) {
            const errorMessage: string = '' + error;
            const headline: string = t('Something went wrong');
            return UIHelper.showMultilineMessage(
                errorMessage.replace('PConObj.WS_CreateVariant error: ', '').replace(/(?:\r\n|\r|\n)/g, '\n'),
                headline,
                () => {
                    // do nothing
                }
            );
        }
    }

    private static async getVariantID(): Promise<VariantObject | null> {
        if (!KompanBase.openedByQuoteIt) {
            return (null);
        }
        let tVariant: VariantObject = KompanStore.getState().variant as VariantObject;
        if (tVariant) {
            return tVariant;
        }
        tVariant = await KompanBase.Configuration.WS_GetNextVariantId();
        KompanHelper.storeValue('variant', tVariant);
        return tVariant;
    }

    private static async reopenOBXFile(pReopenFile: string): Promise<void> {
        const tObxUrl: string = KompanHelper.convertNetworkPathToUrl(pReopenFile);
        const tErrorMessage: string = t('Error while loading old variant. The configurator will be closed.');

        try {
            for (const tE of GlobalHelper.coreApp.model.elements) {
                GlobalHelper.coreApp.model.removeElement(tE);
            }
            await GlobalHelper.articleManager.synchronizeSession(false);

            const tBuffer: ArrayBuffer = await DownloadHelper.getContent(tObxUrl);
            const tBlob: Blob = new Blob([tBuffer]);

            // upload obx
            const tUrl: string | null = await GlobalHelper.eaiwsSession.uploadFile('CutBuffer', tBlob);
            if (utils.string.isNullOrEmpty(tUrl)) {
                throw new Error('Failed to upload obx.');
            }

            // paste obx
            const tPastedIds: Array<string> = await GlobalHelper.eaiwsSession.basket.paste(null, null, tUrl);
            if (tPastedIds == null || tPastedIds.length === 0) {
                throw new Error('No articles found.');
            }

            const tMainArticleElement: cf.MainArticleElement = await GlobalHelper.articleManager.createArticleById(tPastedIds[0]);

            if (tMainArticleElement.hasPlaceholderGeometry) {
                throw new Error('Placeholder.');
            }

            GlobalHelper.coreApp.model.addElement(tMainArticleElement);

            const tElement: core.mdl.SceneElement = GlobalHelper.coreApp.model.elements[0];
            const tView: core.view.View | null = GlobalHelper.coreApp.viewer.activeView;
            if (tView) {
                tView.cameraControl.zoomToFitElements([tElement]);
            }

            tElement.canSelect = (): boolean => { return (false); };
            GlobalHelper.coreApp.viewer.requestRenderFrame();
        } catch (pError) {
            return UIHelper.showMessage(tErrorMessage, () => {
                window.close();
            });
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Kompan/KompanInterface.ts